import { useState, useEffect } from 'react';
import { XMarkIcon, CheckIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '../../ui/Button';
import type { TemplateShare, HomeworkTemplate } from '../../../types/stepWork';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase/config';

interface TemplatePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  share: TemplateShare;
  onAccept: (shareId: string) => Promise<void>;
  onDecline: (shareId: string) => Promise<void>;
}

export function TemplatePreviewModal({
  isOpen,
  onClose,
  share,
  onAccept,
  onDecline,
}: TemplatePreviewModalProps) {
  const [template, setTemplate] = useState<HomeworkTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);

  useEffect(() => {
    if (isOpen && share) {
      loadTemplatePreview();
    }
  }, [isOpen, share]);

  async function loadTemplatePreview() {
    try {
      setLoading(true);
      setError(null);

      // Fetch original template
      const templateRef = doc(
        db,
        'users',
        share.fromUserId,
        'homeworkTemplates',
        share.templateId
      );
      const templateSnap = await getDoc(templateRef);

      if (!templateSnap.exists()) {
        setError('Template no longer available');
        return;
      }

      const templateData = {
        id: templateSnap.id,
        ...templateSnap.data(),
      } as HomeworkTemplate;

      setTemplate(templateData);
    } catch (err) {
      console.error('Error loading template preview:', err);
      setError('Failed to load template preview');
    } finally {
      setLoading(false);
    }
  }

  async function handleAccept() {
    try {
      setAccepting(true);
      await onAccept(share.id);
    } catch (err) {
      console.error('Error accepting share:', err);
    } finally {
      setAccepting(false);
    }
  }

  async function handleDecline() {
    try {
      setDeclining(true);
      await onDecline(share.id);
    } catch (err) {
      console.error('Error declining share:', err);
    } finally {
      setDeclining(false);
    }
  }

  if (!isOpen) return null;

  const stepCount = template ? Object.keys(template.stepAssignments).length : 0;
  const totalAssignments = template
    ? Object.values(template.stepAssignments).reduce(
        (sum, assignments) => sum + assignments.length,
        0
      )
    : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-slate-200 dark:border-navy-700">
          <div>
            <h2 className="text-xl font-semibold text-navy-900 dark:text-navy-100">
              Template Preview
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Shared by {share.fromUserName}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={accepting || declining}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
            </div>
          ) : error ? (
            <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4">
              <p className="text-sm text-danger-700 dark:text-danger-300">{error}</p>
            </div>
          ) : template ? (
            <div className="space-y-6">
              {/* Template Info */}
              <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-4 border border-teal-200 dark:border-teal-800">
                <h3 className="font-semibold text-navy-900 dark:text-navy-100 mb-1">
                  {template.name}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                  {template.fellowship}
                </p>
                <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-500">
                  <span>{totalAssignments} assignments</span>
                  <span>•</span>
                  <span>{stepCount} step{stepCount !== 1 ? 's' : ''}</span>
                </div>
              </div>

              {/* Step Assignments */}
              <div>
                <h4 className="text-sm font-semibold text-navy-900 dark:text-navy-100 mb-3">
                  Assignments by Step
                </h4>
                <div className="space-y-3">
                  {Object.entries(template.stepAssignments)
                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                    .map(([step, assignments]) => (
                      <div
                        key={step}
                        className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-navy-900 dark:text-navy-100">
                            Step {step}
                          </h5>
                          <span className="text-xs text-slate-500 dark:text-slate-500">
                            {assignments.length} assignment{assignments.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <ul className="space-y-1">
                          {assignments.map((assignment, idx) => (
                            <li
                              key={idx}
                              className="text-sm text-slate-700 dark:text-slate-300 flex items-start gap-2"
                            >
                              <span className="text-teal-500 mt-1">•</span>
                              <span className="flex-1">{assignment.description}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Note:</strong> Accepting this template will create an independent copy
                  in your library. You can customize it freely—changes won't affect the original.
                </p>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {!loading && !error && (
          <div className="flex gap-3 p-6 border-t border-slate-200 dark:border-navy-700">
            <Button
              variant="secondary"
              onClick={handleDecline}
              disabled={accepting || declining}
              className="flex-1"
              leftIcon={<XCircleIcon className="w-4 h-4" />}
            >
              {declining ? 'Declining...' : 'Decline'}
            </Button>
            <Button
              variant="primary"
              onClick={handleAccept}
              disabled={accepting || declining}
              className="flex-1"
              leftIcon={<CheckIcon className="w-4 h-4" />}
            >
              {accepting ? 'Accepting...' : 'Accept Template'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
